import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useSubscription } from '@/hooks/useSubscription'
import { Content, Section, AccessManager, DEFAULT_SECTIONS } from '@/services/homepage'
import { getAllProviderVideos } from '@/services/adultContent'
import { buildSubscriptionUrl } from '@/utils/subscriptionRedirect'
import './HomePage.css'

const HomePage = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { hasActiveSubscription, activeSubscription, loading: subscriptionLoading, hasAccessLevel } = useSubscription()
  
  // Debug: afficher le statut de l'abonnement
  React.useEffect(() => {
    if (!subscriptionLoading) {
      console.log('HomePage - Subscription status:', {
        hasActiveSubscription: hasActiveSubscription(),
        activeSubscription,
        loading: subscriptionLoading
      })
    }
  }, [activeSubscription, subscriptionLoading, hasActiveSubscription])
  
  // Récupération des vidéos depuis les différents providers
  const { data: sections, isLoading, error } = useQuery({
    queryKey: ['provider-videos', 'homepage'],
    queryFn: async () => {
      try {
        // Récupérer les vidéos de tous les providers
        const videosByProvider = await getAllProviderVideos()
        
        // Mapper les sections par défaut avec les vidéos récupérées
        const sectionsWithVideos: Section[] = DEFAULT_SECTIONS.map((section): Section => {
          let videos: Content[] = []
          
          switch (section.id) {
            case 'eporner':
              videos = videosByProvider.eporner
              break
            case 'letsjerktv':
              videos = videosByProvider.letsjerktv
              break
            case 'redtube':
              videos = videosByProvider.redtube
              break
          }
          
          return {
            id: section.id!,
            title: section.title!,
            description: section.description!,
            maxFreeVideos: section.maxFreeVideos!,
            videos
          }
        })
        
        return sectionsWithVideos
      } catch (err) {
        console.error('Erreur lors de la récupération des vidéos:', err)
        // Fallback vers les sections vides
        return DEFAULT_SECTIONS.map((section): Section => ({
          id: section.id!,
          title: section.title!,
          description: section.description!,
          maxFreeVideos: section.maxFreeVideos!,
          videos: []
        }))
      }
    },
    // Utiliser les sections par défaut comme données de fallback
    initialData: DEFAULT_SECTIONS.map((section): Section => ({
      id: section.id!,
      title: section.title!,
      description: section.description!,
      maxFreeVideos: section.maxFreeVideos!,
      videos: []
    }))
  })

  const getVideoThumbnailUrl = (video: Content): string | undefined => {
    if (!video?.thumbnailS3Key || typeof video.thumbnailS3Key !== 'string') {
      return undefined
    }

    return video.thumbnailS3Key.startsWith('http')
      ? video.thumbnailS3Key
      : `/api/contents/${video.id}/thumbnail`
  }

  const buildVideoRedirectUrl = (video: Content, section?: Section): string => {
    return buildSubscriptionUrl({
      from: 'video',
      videoId: video.id,
      videoTitle: video.title,
      videoThumbnail: getVideoThumbnailUrl(video),
      videoProvider: video.provider,
      sectionTitle: section?.title,
    })
  }

  const handleVideoClick = (video: Content, sectionIndex: number, videoIndex: number) => {
    const section = sections?.[sectionIndex]
    if (!section || !video?.id) {
      console.warn('Impossible de naviguer : section ou vidéo invalide', { section, video })
      return
    }

    // Les 3 premières vidéos sont gratuites (basic)
    const isFirstVideos = videoIndex < 3
    // Pour le reste, vérifier si l'utilisateur a au moins le niveau premium
    const hasPremiumAccess = hasAccessLevel('premium')
    const hasAccess = isFirstVideos || hasPremiumAccess

    // Log pour debug
    console.log('Video click:', {
      videoIndex,
      maxFreeVideos: section.maxFreeVideos,
      hasAccess,
      videoId: video.id,
      provider: video.provider,
      title: video.title
    })

    if (hasAccess) {
      // Navigation vers la page de contenu avec l'ID du provider
      console.log('Navigation vers:', `/content/${video.id}`)
      navigate(`/content/${video.id}`)
    } else {
      // Rediriger vers la page de souscription
      console.log('Redirection vers /subscriptions (accès refusé)')
      navigate(buildVideoRedirectUrl(video, section))
    }
  }

  if (isLoading) {
    return <div className="homepage-loading">Chargement des contenus...</div>
  }

  if (error) {
    return <div className="homepage-error">Erreur lors du chargement du contenu</div>
  }

  const location = useLocation()
  const canonicalUrl = `https://www.edenplaisir.com${location.pathname === '/' ? '' : location.pathname}`.replace(/\/$/, '') || 'https://www.edenplaisir.com'

  return (
    <div className="homepage">
      <Helmet>
        <title>EdenPlaisir.com - Plateforme de contenus premium</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content="Découvrez un univers de contenus premium adaptés à vos envies sur EdenPlaisir.com. Accédez à une vaste collection de vidéos de qualité." />
        <meta name="keywords" content="contenus premium, vidéos, abonnement, EdenPlaisir" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="EdenPlaisir.com - Plateforme de contenus premium" />
        <meta property="og:description" content="Découvrez un univers de contenus premium adaptés à vos envies sur EdenPlaisir.com." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="EdenPlaisir.com - Plateforme de contenus premium" />
        <meta name="twitter:description" content="Découvrez un univers de contenus premium adaptés à vos envies sur EdenPlaisir.com." />
      </Helmet>
      
      <div className="homepage-hero">
        <h1>Bienvenue sur EdenPlaisir.com</h1>
        <p>Découvrez un univers de contenus premium adaptés à vos envies</p>
      </div>

      {sections && sections.length > 0 ? (
        <div className="homepage-sections">
          {sections.map((section, sectionIndex) => (
            <div key={section.id} className="content-section">
              <div className="section-header">
                <div className="section-header-content">
                  <div>
                    <h2 className="section-title">{section.title}</h2>
                    <p className="section-description">{section.description}</p>
                  </div>
                  <button 
                    className="section-more-button"
                    onClick={() => navigate('/categories')}
                  >
                    Voir plus
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
              
              {section.videos && section.videos.length > 0 ? (
                <div className="videos-grid">
                    {section.videos.map((video, videoIndex) => {
                      // Les 3 premières vidéos sont gratuites (basic)
                      const isFirstVideos = videoIndex < 3
                      // Pour le reste, vérifier si l'utilisateur a au moins le niveau premium
                      const hasPremiumAccess = hasAccessLevel('premium')
                      const hasAccess = isFirstVideos || hasPremiumAccess
                      
                      return (
                        <div 
                          key={video.id} 
                          className={`video-card ${!hasAccess ? 'premium' : ''}`}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            console.log('Clic détecté sur la carte vidéo:', {
                              videoId: video.id,
                              hasAccess,
                              provider: video.provider
                            })
                            handleVideoClick(video, sectionIndex, videoIndex)
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="video-thumbnail-container">
                            {video.thumbnailS3Key && typeof video.thumbnailS3Key === 'string' && video.thumbnailS3Key.startsWith('http') ? (
                              <img
                                src={video.thumbnailS3Key}
                                alt={video.title}
                                className="video-thumbnail"
                                onError={(e) => {
                                  // En cas d'erreur, masquer l'image et afficher le placeholder
                                  const target = e.target as HTMLImageElement
                                  target.style.display = 'none'
                                  const container = target.parentElement
                                  const placeholder = container?.querySelector('.video-thumbnail.placeholder') as HTMLElement
                                  if (placeholder) {
                                    placeholder.style.display = 'flex'
                                  }
                                }}
                              />
                            ) : video.thumbnailS3Key && typeof video.thumbnailS3Key === 'string' ? (
                              <img
                                src={`/api/contents/${video.id}/thumbnail`}
                                alt={video.title}
                                className="video-thumbnail"
                              />
                            ) : null}
                            {(!video.thumbnailS3Key || typeof video.thumbnailS3Key !== 'string' || !video.thumbnailS3Key.startsWith('http')) && (
                              <div className="video-thumbnail placeholder" style={{ display: video.thumbnailS3Key && typeof video.thumbnailS3Key === 'string' && !video.thumbnailS3Key.startsWith('http') ? 'none' : 'flex' }}>
                                <div className="placeholder-content">
                                  <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                </div>
                              </div>
                            )}
                            
                            {/* Icône de lecture par-dessus l'image */}
                            {(video.thumbnailS3Key && typeof video.thumbnailS3Key === 'string' && video.thumbnailS3Key.startsWith('http')) || 
                             (video.thumbnailS3Key && typeof video.thumbnailS3Key === 'string' && !video.thumbnailS3Key.startsWith('http')) ? (
                              <div className="video-play-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                              </div>
                            ) : null}
                            
                            {/* Badge premium pour les vidéos payantes */}
                            {!hasAccess && (
                              <div className="premium-badge">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 1l3 6h6l-5 5 2 6-6-3-6 3 2-6-5-5h6l3-6z"/>
                                </svg>
                                Premium
                              </div>
                            )}
                            
                            {/* Overlay sur les vidéos premium */}
                            {!hasAccess && (
                              <div 
                                className="premium-overlay"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(buildVideoRedirectUrl(video, section))
                                }}
                              >
                                <div className="lock-icon">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                                  </svg>
                                </div>
                                <span>Abonnement requis</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="video-info">
                            <h3 className="video-title">{video.title}</h3>
                            {/* Ne pas afficher la description pour Eporner */}
                            {video.description && video.provider !== 'eporner' && (
                              <p className="video-description">{video.description}</p>
                            )}
                            <div className="video-meta">
                              {video.duration && (
                                <span className="video-duration">{AccessManager.formatDuration(video.duration)}</span>
                              )}
                              {video.views !== undefined && video.views !== null && (
                                <span className="video-views">
                                  {video.views >= 1000000 
                                    ? `${(video.views / 1000000).toFixed(1)}M` 
                                    : video.views >= 1000 
                                      ? `${(video.views / 1000).toFixed(1)}K`
                                      : video.views.toLocaleString()}
                                  {' vues'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </div>
              ) : (
                <div className="no-content">
                  <p>Aucune vidéo disponible dans cette section pour le moment</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="no-content">
          <p>Aucun contenu disponible</p>
        </div>
      )}
    </div>
  )
}

export default HomePage
